import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { api } from "@/lib/api";

// CEO Rev 1.0 Phase 2 — viewer-facing survey capture. Shows the active surveys
// for this pack's campaign (e.g. a Post-Onboarding Review) and submits answers.
type Question = {
  id: string;
  prompt: string;
  type: string;
  options: string[] | null;
  required: boolean;
};
type ViewerSurvey = { id: string; name: string; description: string | null; questions: Question[] };

export function ViewerSurveys({ token }: { token: string | undefined }) {
  const { data } = useQuery({
    queryKey: ["viewer-surveys", token],
    queryFn: () => api.get<{ items: ViewerSurvey[] }>(`/api/view/${token}/surveys`),
    enabled: !!token,
  });
  const surveys = data?.items ?? [];
  if (!token || surveys.length === 0) return null;
  return (
    <>
      {surveys.map((s) => (
        <SurveyCard key={s.id} token={token} survey={s} />
      ))}
    </>
  );
}

function SurveyCard({ token, survey }: { token: string; survey: ViewerSurvey }) {
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [done, setDone] = useState(false);
  const setA = (qid: string, v: unknown) => setAnswers((a) => ({ ...a, [qid]: v }));
  const toggleMulti = (qid: string, opt: string) =>
    setAnswers((a) => {
      const cur = Array.isArray(a[qid]) ? (a[qid] as string[]) : [];
      return { ...a, [qid]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });

  const submit = useMutation({
    mutationFn: () => api.post(`/api/view/${token}/surveys/${survey.id}/respond`, { answers }),
    onSuccess: () => {
      setDone(true);
      toast.success("Thanks for your feedback!");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Submit failed"),
  });

  const missingRequired = survey.questions.some((q) => {
    if (!q.required) return false;
    const v = answers[q.id];
    return v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
  });

  if (done) {
    return (
      <section className="bg-viewer-soft rounded-md p-5 text-center">
        <div className="font-head text-lg">Thanks — your response to "{survey.name}" was recorded.</div>
      </section>
    );
  }

  return (
    <section className="bg-viewer-soft rounded-md p-5 space-y-4">
      <div>
        <div className="font-head text-lg">{survey.name}</div>
        {survey.description && <p className="text-sm text-navy-200">{survey.description}</p>}
      </div>
      {survey.questions.map((q) => (
        <div key={q.id} className="space-y-1.5">
          <label className="text-sm font-medium">
            {q.prompt}
            {q.required && <span className="text-gold"> *</span>}
          </label>
          {q.type === "short_text" && (
            <Input value={(answers[q.id] as string) ?? ""} onChange={(e) => setA(q.id, e.target.value)} />
          )}
          {q.type === "long_text" && (
            <Textarea rows={3} value={(answers[q.id] as string) ?? ""} onChange={(e) => setA(q.id, e.target.value)} />
          )}
          {q.type === "boolean" && (
            <div className="flex gap-2">
              {[["Yes", true], ["No", false]].map(([label, val]) => (
                <Button
                  key={String(label)}
                  type="button"
                  size="sm"
                  variant={answers[q.id] === val ? "primary" : "outline"}
                  onClick={() => setA(q.id, val)}
                >
                  {label as string}
                </Button>
              ))}
            </div>
          )}
          {q.type === "rating" && (
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Button
                  key={n}
                  type="button"
                  size="sm"
                  variant={answers[q.id] === n ? "primary" : "outline"}
                  onClick={() => setA(q.id, n)}
                >
                  {n}
                </Button>
              ))}
            </div>
          )}
          {q.type === "single_choice" &&
            (q.options ?? []).map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name={q.id}
                  checked={answers[q.id] === opt}
                  onChange={() => setA(q.id, opt)}
                />
                {opt}
              </label>
            ))}
          {q.type === "multi_choice" &&
            (q.options ?? []).map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Array.isArray(answers[q.id]) && (answers[q.id] as string[]).includes(opt)}
                  onChange={() => toggleMulti(q.id, opt)}
                />
                {opt}
              </label>
            ))}
        </div>
      ))}
      <Button disabled={missingRequired || submit.isPending} onClick={() => submit.mutate()}>
        {submit.isPending ? "Submitting…" : "Submit"}
      </Button>
    </section>
  );
}
